import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Wand2, ShieldAlert, Save } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { v: "off", l: "Off", d: "No AI contractor recommendations are used." },
  { v: "recommend_only", l: "Recommend Only", d: "AI ranks contractors; SJ staff selects (default)." },
  { v: "auto_offer", l: "Auto-Offer", d: "Eligible contractors are offered the job and must accept." },
  { v: "auto_assign", l: "Auto-Assign", d: "System auto-assigns the top eligible contractor when all rules pass." },
];

export default function DispatchSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = user.role === "admin";

  useEffect(() => { api.get("/admin/dispatch-settings").then(({ data }) => setS(data)).catch(() => setS(false)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { mode: s.mode, min_score: Number(s.min_score), min_rating: Number(s.min_rating), max_workload: Number(s.max_workload),
        require_insurance: s.require_insurance, require_license: s.require_license, emergency_auto: s.emergency_auto,
        max_offers: Number(s.max_offers), offer_timeout_min: Number(s.offer_timeout_min) };
      const { data } = await api.put("/admin/dispatch-settings", body);
      setS(data); toast.success("Dispatch settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  if (s === false) return <DashboardLayout nav={getNav(user.role)} title="Dispatch Settings"><p>Unable to load.</p></DashboardLayout>;

  return (
    <DashboardLayout nav={getNav(user.role)} title="AI Dispatch Settings">
      {!s ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="max-w-2xl space-y-6">
          <p className="text-sm text-slate-500 flex items-center gap-1.5"><Wand2 className="w-4 h-4 text-indigo-500" /> Control how AI assists dispatch. Auto-Assign is never on by default, and emergency/high-risk requests always route to human review unless explicitly allowed below.</p>

          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
            <Label>Dispatch Mode</Label>
            <div className="grid gap-2">
              {MODES.map((m) => (
                <label key={m.v} data-testid={`mode-${m.v}`} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${s.mode === m.v ? "border-indigo-400 bg-indigo-50" : "border-slate-200"} ${!isAdmin ? "opacity-60 pointer-events-none" : ""}`}>
                  <input type="radio" name="mode" checked={s.mode === m.v} onChange={() => setS({ ...s, mode: m.v })} className="mt-1" />
                  <div><div className="font-medium text-slate-900 text-sm">{m.l}</div><div className="text-xs text-slate-500">{m.d}</div></div>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h3 className="font-head font-semibold text-slate-900">Auto-Dispatch Rules</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label>Min match %</Label><Input data-testid="rule-min-score" type="number" value={s.min_score} onChange={(e) => setS({ ...s, min_score: e.target.value })} className="mt-1.5" disabled={!isAdmin} /></div>
              <div><Label>Min rating</Label><Input type="number" value={s.min_rating} onChange={(e) => setS({ ...s, min_rating: e.target.value })} className="mt-1.5" disabled={!isAdmin} /></div>
              <div><Label>Max workload</Label><Input data-testid="rule-max-workload" type="number" value={s.max_workload} onChange={(e) => setS({ ...s, max_workload: e.target.value })} className="mt-1.5" disabled={!isAdmin} /></div>
              <div><Label>Max offers</Label><Input type="number" value={s.max_offers} onChange={(e) => setS({ ...s, max_offers: e.target.value })} className="mt-1.5" disabled={!isAdmin} /></div>
              <div><Label>Offer timeout (min)</Label><Input type="number" value={s.offer_timeout_min} onChange={(e) => setS({ ...s, offer_timeout_min: e.target.value })} className="mt-1.5" disabled={!isAdmin} /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3"><Label className="mb-0">Require valid insurance</Label><Switch data-testid="rule-insurance" checked={s.require_insurance} onCheckedChange={(v) => setS({ ...s, require_insurance: v })} disabled={!isAdmin} /></div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3"><Label className="mb-0">Require valid license</Label><Switch checked={s.require_license} onCheckedChange={(v) => setS({ ...s, require_license: v })} disabled={!isAdmin} /></div>
            <div className="flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50 p-3">
              <div className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-rose-600" /><Label className="mb-0 text-rose-800">Allow emergency auto-dispatch</Label></div>
              <Switch data-testid="rule-emergency-auto" checked={s.emergency_auto} onCheckedChange={(v) => setS({ ...s, emergency_auto: v })} disabled={!isAdmin} />
            </div>
            <p className="text-xs text-slate-400">Contractors with expired required documents are never auto-offered or auto-assigned.</p>
          </div>

          {isAdmin ? (
            <Button data-testid="save-dispatch-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save Settings</>}</Button>
          ) : <p className="text-sm text-slate-400">Only Admins can change dispatch settings.</p>}
        </div>
      )}
    </DashboardLayout>
  );
}
